/**
 * ConToaster — the console-scoped toast region.
 *
 * Mounted once (by ``ConsoleCommandCenter``, inside the console theme
 * provider). Renders through a portal on ``document.body`` and STAMPS
 * ``useConsoleThemeClass()`` on the portal root — the same rule ``ConModal``
 * follows — so the ``--con-*`` variables (and the ``.dark`` flip) reach a
 * surface that lives outside the console root.
 *
 * Pencil treatment: a PAPER surface (``--con-surface-raised``) lifted by a
 * WARM shadow-as-border (a 1px hairline ring + a soft brown drop shadow) —
 * never a saturated colored background. Tone is carried by a thin left
 * accent bar + the leading icon in ``--con-success/-danger-text`` /
 * ``--con-accent-text``, not by a fill. ``aria-live`` announces each toast;
 * entrance reuses ``con-page-enter`` (disabled under reduced motion by the
 * shared CSS media query).
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@ando/ui";

import { type ConToast, type ConToastTone, useConsoleToastStore } from "../lib/toast";
import { useConsoleThemeClass } from "../theme/ConsoleTheme";
import { conFocusRing } from "./atoms";

const TONE_ICON: Record<ConToastTone, LucideIcon> = {
  success: CheckCircle2,
  danger: AlertCircle,
  info: Info,
};

const TONE_TEXT: Record<ConToastTone, string> = {
  success: "text-[color:var(--con-success-text)]",
  danger: "text-[color:var(--con-danger-text)]",
  info: "text-[color:var(--con-accent-text)]",
};

const TONE_BAR: Record<ConToastTone, string> = {
  success: "bg-[color:var(--con-success-text)]",
  danger: "bg-[color:var(--con-danger-text)]",
  info: "bg-[color:var(--con-accent)]",
};

function ToastRow({ toast }: { toast: ConToast }): React.ReactElement {
  const { i18n } = useLingui();
  const dismiss = useConsoleToastStore((s) => s.dismiss);
  const Icon = TONE_ICON[toast.tone];

  React.useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <div
      className={cn(
        "con-page-enter pointer-events-auto relative flex w-80 max-w-[calc(100vw-2rem)] items-start gap-2.5",
        "overflow-hidden rounded-md bg-[color:var(--con-surface-raised)] py-2.5 pr-2.5 pl-3.5",
        // Warm shadow-as-border: 1px hairline ring + soft brown drop.
        "shadow-[0_0_0_1px_var(--con-border),0_12px_28px_-12px_rgba(31,29,26,0.32)]",
      )}
    >
      {/* Thin tone accent bar — the color cue, not a fill. */}
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-0.5", TONE_BAR[toast.tone])} />
      <Icon
        aria-hidden
        size={15}
        strokeWidth={2}
        className={cn("mt-px shrink-0", TONE_TEXT[toast.tone])}
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface)] leading-snug">
          {toast.title}
        </p>
        {toast.description ? (
          <p className="mt-0.5 text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)] leading-snug">
            {toast.description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label={i18n._(msg`Chiudi la notifica`)}
        className={cn(
          "con-press shrink-0 rounded-sm p-0.5 text-[color:var(--con-on-surface-faint)]",
          "transition-colors duration-150 ease-out hover:text-[color:var(--con-on-surface)]",
          conFocusRing,
        )}
      >
        <X size={13} strokeWidth={1.75} aria-hidden />
      </button>
    </div>
  );
}

export function ConToaster(): React.ReactElement | null {
  const { i18n } = useLingui();
  const themeClass = useConsoleThemeClass();
  const toasts = useConsoleToastStore((s) => s.toasts);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        themeClass,
        "pointer-events-none fixed right-4 bottom-4 z-[70] flex flex-col items-end gap-2",
      )}
      role="region"
      aria-label={i18n._(msg`Notifiche della console`)}
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} />
      ))}
    </div>,
    document.body,
  );
}
